import { supabase } from '../lib/supabase'
import { AircraftNote, AircraftNoteInput, AircraftNoteUpdate } from '../types/database'

export class NotesService {
  /**
   * Get note counts for multiple aircraft IDs in a single query.
   * Returns a Map of aircraftId -> note count.
   */
  static async getNoteCountsByAircraftIds(aircraftIds: number[]): Promise<Map<number, number>> {
    const counts = new Map<number, number>()
    if (aircraftIds.length === 0) return counts

    try {
      const { data, error } = await supabase
        .from('aircraft_notes')
        .select('aircraft_id')
        .in('aircraft_id', aircraftIds)

      if (error) {
        // If table doesn't exist, return empty map
        if (error.message?.includes('relation "aircraft_notes" does not exist') ||
            error.message?.includes('table "aircraft_notes" does not exist')) {
          return counts
        }
        throw new Error(`Failed to fetch note counts: ${error.message}`)
      }

      if (data) {
        data.forEach(row => {
          counts.set(row.aircraft_id, (counts.get(row.aircraft_id) || 0) + 1)
        })
      }

      return counts
    } catch (error) {
      // If it's a table not found error, return empty map
      if (error instanceof Error &&
          (error.message?.includes('relation "aircraft_notes" does not exist') ||
           error.message?.includes('table "aircraft_notes" does not exist'))) {
        return counts
      }
      throw error
    }
  }

  /**
   * Get all notes for a specific aircraft, ordered by creation date (newest first)
   */
  static async getByAircraftId(aircraftId: number): Promise<AircraftNote[]> {
    try {
      const { data, error } = await supabase
        .from('aircraft_notes')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('created_at', { ascending: false })

      if (error) {
        throw new Error(`Failed to fetch notes: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get recent notes for a specific aircraft (limited to specified count)
   */
  static async getRecentByAircraftId(aircraftId: number, limit: number = 10): Promise<AircraftNote[]> {
    try {
      const { data, error } = await supabase
        .from('aircraft_notes')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        throw new Error(`Failed to fetch recent notes: ${error.message}`)
      }

      return data || []
    } catch (error) {
      throw error
    }
  }

  /**
   * Get a specific note by ID
   */
  static async getById(noteId: string): Promise<AircraftNote | null> {
    try {
      const { data, error } = await supabase
        .from('aircraft_notes')
        .select('*')
        .eq('id', noteId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // Note not found
        }
        throw new Error(`Failed to fetch note: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create a new note
   */
  static async create(noteInput: AircraftNoteInput): Promise<AircraftNote> {
    try {
      const insertData: Record<string, any> = {
        aircraft_id: noteInput.aircraft_id,
        content: noteInput.content,
        created_by: (await supabase.auth.getUser()).data.user?.id
      }

      // Allow custom timestamp if provided
      if (noteInput.created_at) {
        insertData.created_at = noteInput.created_at
      }

      const { data, error } = await supabase
        .from('aircraft_notes')
        .insert(insertData)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create note: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Update an existing note
   */
  static async update(noteId: string, noteUpdate: AircraftNoteUpdate): Promise<AircraftNote> {
    try {
      const updateData: Record<string, any> = {
        content: noteUpdate.content
      }

      // Allow timestamp override if provided
      if (noteUpdate.created_at) {
        updateData.created_at = noteUpdate.created_at
      }

      const { data, error } = await supabase
        .from('aircraft_notes')
        .update(updateData)
        .eq('id', noteId)
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to update note: ${error.message}`)
      }

      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete a note
   */
  static async delete(noteId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('aircraft_notes')
        .delete()
        .eq('id', noteId)

      if (error) {
        throw new Error(`Failed to delete note: ${error.message}`)
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Check if user can edit/delete a note
   * Returns true if user is admin or is the author within 30 minutes
   */
  static canEditNote(note: AircraftNote, userId: string, isAdmin: boolean): boolean {
    if (isAdmin) {
      return true
    }

    if (note.created_by !== userId) {
      return false
    }

    // Check if note was created within 30 minutes
    const createdAt = new Date(note.created_at)
    const now = new Date()
    const thirtyMinutesAgo = new Date(now.getTime() - 30 * 60 * 1000)

    return createdAt > thirtyMinutesAgo
  }

  /**
   * Enrich notes with author display names from profiles.
   * Shared helper for getByAircraftIdWithAuthors and getRecentByAircraftIdWithAuthors.
   */
  private static async enrichNotesWithAuthors(notes: AircraftNote[]): Promise<(AircraftNote & { author_name: string })[]> {
    // Get unique user IDs
    const userIds = [...new Set(notes.map(note => note.created_by))]

    // Fetch profiles for these users
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, display_name')
      .in('id', userIds)

    if (profilesError) {
      // Continue without author names rather than failing completely
    }

    // Create a map of user ID to display name
    const profileMap = new Map()
    if (profiles) {
      profiles.forEach(profile => {
        profileMap.set(profile.id, profile.display_name || 'Unknown User')
      })
    }

    // Combine notes with author names
    return notes.map(note => ({
      ...note,
      author_name: profileMap.get(note.created_by) || 'Unknown User'
    }))
  }

  /**
   * Get notes with author information (display_name from profiles)
   */
  static async getByAircraftIdWithAuthors(aircraftId: number): Promise<(AircraftNote & { author_name: string })[]> {
    try {
      const { data: notes, error: notesError } = await supabase
        .from('aircraft_notes')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('created_at', { ascending: false })

      if (notesError) {
        if (notesError.message?.includes('relation "aircraft_notes" does not exist') ||
            notesError.message?.includes('table "aircraft_notes" does not exist')) {
          return []
        }
        throw new Error(`Failed to fetch notes: ${notesError.message}`)
      }

      if (!notes || notes.length === 0) {
        return []
      }

      return this.enrichNotesWithAuthors(notes)
    } catch (error) {
      if (error instanceof Error &&
          (error.message?.includes('relation "aircraft_notes" does not exist') ||
           error.message?.includes('table "aircraft_notes" does not exist'))) {
        return []
      }
      throw error
    }
  }

  /**
   * Get recent notes with author information
   */
  static async getRecentByAircraftIdWithAuthors(aircraftId: number, limit: number = 10): Promise<(AircraftNote & { author_name: string })[]> {
    try {
      const { data: notes, error: notesError } = await supabase
        .from('aircraft_notes')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (notesError) {
        if (notesError.message?.includes('relation "aircraft_notes" does not exist') ||
            notesError.message?.includes('table "aircraft_notes" does not exist')) {
          return []
        }
        throw new Error(`Failed to fetch recent notes: ${notesError.message}`)
      }

      if (!notes || notes.length === 0) {
        return []
      }

      return this.enrichNotesWithAuthors(notes)
    } catch (error) {
      if (error instanceof Error &&
          (error.message?.includes('relation "aircraft_notes" does not exist') ||
           error.message?.includes('table "aircraft_notes" does not exist'))) {
        return []
      }
      throw error
    }
  }
}
